import { Controller } from "@hotwired/stimulus"
import { loadPlan, savePlan } from "plan_storage"
import { renderIcons } from "lucide_icons"

// IPP Meeting Card — a modal where the family proposes 2–3 IPP meeting times
// and (optionally) flags that they intend to record the meeting, then downloads
// a professional one-page "IPP Meeting Request" letter (PDF) to send to their
// Regional Center Service Coordinator. State persists on plan.meta.ippMeeting;
// the PDF is rendered server-side and captured by headless Chrome via
// /api/generate-ipp-pdf (mirrors the Save-as-PDF flow).
const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, c => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
}[c]))
const SLOT_COUNT = 3

export default class extends Controller {
  static targets = ["dialog", "body"]

  open() {
    this._render()
    if (!this.hasDialogTarget) return
    if (typeof this.dialogTarget.showModal === "function") this.dialogTarget.showModal()
    else this.dialogTarget.setAttribute("open", "")
  }

  close() {
    if (!this.hasDialogTarget) return
    if (typeof this.dialogTarget.close === "function") this.dialogTarget.close()
    else this.dialogTarget.removeAttribute("open")
  }

  // Click on the dialog's backdrop (the <dialog> element itself, outside the
  // form panel) closes it.
  backdrop(event) {
    if (event.target === this.dialogTarget) this.close()
  }

  _ipp() {
    const plan = this._load()
    const ipp = (plan.meta && plan.meta.ippMeeting) || {}
    const slots = Array.isArray(ipp.slots) ? ipp.slots.slice(0, SLOT_COUNT).map(s => ({
      date: (s && s.date) || "", time: (s && s.time) || ""
    })) : []
    while (slots.length < SLOT_COUNT) slots.push({ date: "", time: "" })
    return { recordMeeting: !!ipp.recordMeeting, slots }
  }

  _render() {
    if (!this.hasBodyTarget) return
    const ipp = this._ipp()
    const rows = ipp.slots.map((s, i) => `
      <div class="ipp-slot">
        <span class="ipp-slot-num">${i + 1}</span>
        <label class="ipp-field">
          <span>Date</span>
          <input type="date" data-ipp-field="date" data-ipp-index="${i}" value="${esc(s.date)}">
        </label>
        <label class="ipp-field">
          <span>Time</span>
          <input type="time" data-ipp-field="time" data-ipp-index="${i}" value="${esc(s.time)}">
        </label>
      </div>`).join("")

    this.bodyTarget.innerHTML = `
      <div class="ipp-head">
        <h2><i data-lucide="calendar-clock"></i> IPP Meeting Card</h2>
        <button type="button" class="ipp-close" data-ipp-close aria-label="Close">&times;</button>
      </div>
      <p class="ipp-intro">The Client or Client&rsquo;s Authorized Representative is requesting that an Individual Program Plan (IPP) meeting be scheduled using one of the proposed meeting times below. Add <strong>2&ndash;3</strong> available times, then download the PDF to send to the Regional Center Service Coordinator.</p>
      <div class="ipp-slots">${rows}</div>
      <label class="ipp-record">
        <input type="checkbox" data-ipp-field="record" ${ipp.recordMeeting ? "checked" : ""}>
        <span>The family intends to record the IPP meeting.</span>
      </label>
      <p class="ipp-note" data-ipp-note></p>
      <div class="ipp-actions">
        <button type="button" class="ipp-btn-secondary" data-ipp-close>Close</button>
        <button type="button" class="ipp-btn-primary" data-ipp-download><i data-lucide="download"></i> Download PDF</button>
      </div>`

    this.bodyTarget.querySelectorAll("[data-ipp-close]").forEach(b => b.addEventListener("click", () => this.close()))
    this.bodyTarget.querySelector("[data-ipp-download]")?.addEventListener("click", (e) => this.download(e))
    this.bodyTarget.querySelectorAll("[data-ipp-field]").forEach(el => {
      el.addEventListener("change", () => this._persist())
    })
    renderIcons(this.bodyTarget)
  }

  _persist() {
    const plan = this._load()
    if (!plan.meta) plan.meta = {}
    const slots = []
    for (let i = 0; i < SLOT_COUNT; i++) {
      const date = this.bodyTarget.querySelector(`[data-ipp-field="date"][data-ipp-index="${i}"]`)?.value || ""
      const time = this.bodyTarget.querySelector(`[data-ipp-field="time"][data-ipp-index="${i}"]`)?.value || ""
      slots.push({ date, time })
    }
    const record = !!this.bodyTarget.querySelector('[data-ipp-field="record"]')?.checked
    plan.meta.ippMeeting = { slots, recordMeeting: record }
    savePlan(plan)
    return plan
  }

  async download(event) {
    event?.preventDefault()
    const plan = this._persist()
    if (!plan || !plan.client) { this._note("Add some client info first.", true); return }
    const btn = this.bodyTarget.querySelector("[data-ipp-download]")
    const prev = btn ? btn.innerHTML : null
    if (btn) { btn.disabled = true; btn.innerHTML = '<i data-lucide="loader-2" class="ipp-spin"></i> Generating…'; renderIcons(this.bodyTarget) }
    this._note("Generating PDF…")
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch("/api/generate-ipp-pdf", {
        method:  "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept":       "application/pdf",
          ...(csrf ? { "X-CSRF-Token": csrf } : {})
        },
        credentials: "same-origin",
        body: JSON.stringify({ plan })
      })
      if (!res.ok) {
        const d = (await res.text().catch(() => "")).slice(0, 200)
        throw new Error(`server error ${res.status}${d ? ": " + d : ""}`)
      }
      const blob = await res.blob()
      this._downloadBlob(blob, this._filename(plan))
      this._note("PDF downloaded — send it to your Service Coordinator.")
    } catch (err) {
      console.error("[ipp-meeting] PDF failed:", err)
      this._note(`PDF failed — ${err.message}`, true)
    } finally {
      if (btn) { btn.disabled = false; btn.innerHTML = prev; renderIcons(this.bodyTarget) }
    }
  }

  _filename(plan) {
    const c = plan.client || {}
    const name = String(c.name || [c.firstName, c.lastName].filter(Boolean).join(" ") || "Participant")
      .replace(/[^\w\s\-.,()]/g, "").trim()
    return `IPP Meeting Request - ${name}.pdf`
  }

  _downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  _note(msg, isErr) {
    const el = this.hasBodyTarget ? this.bodyTarget.querySelector("[data-ipp-note]") : null
    if (!el) return
    el.textContent = msg
    el.classList.toggle("is-err", !!isErr)
  }

  _load() {
    try { return loadPlan() || {} } catch (_) { return {} }
  }
}
